

import React, { useState, useEffect } from 'react';
import { Box, Container, Alert } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import OrderList from "./Orderlist";
import OrderDetails from './Orderdetails';
import PaymentCollection from './Paymentcollection';
import PickupConfirmation from './Pickupconfirmation';
import DeliveryVerification from './Deliveryverification';
import DeliverySummary from './Deliverysummary';
import IssueReporting from './Issuereporting';

const DeliveryAgentDashboard = () => {
  const navigate = useNavigate();
  const [currentScreen, setCurrentScreen] = useState('orderList');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [agentData, setAgentData] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Helper function to get auth headers
  const getAuthHeaders = () => {
    const userId = sessionStorage.getItem('userId');
    const token = sessionStorage.getItem('token');
    
    const headers = {
      'X-User-Id': userId
    };
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    return headers;
  };

  // Track order progression in localStorage
  const getOrderProgression = (orderId) => {
    const progressionKey = `order_progression_${orderId}`;
    const saved = localStorage.getItem(progressionKey);
    return saved ? JSON.parse(saved) : {
      orderDetailsViewed: false,
      pickupConfirmed: false,
      otpVerified: false,
      paymentCollected: false,
      deliveryCompleted: false
    };
  };

  const updateOrderProgression = (orderId, updates) => {
    const progressionKey = `order_progression_${orderId}`;
    const current = getOrderProgression(orderId);
    const updated = { ...current, ...updates };
    localStorage.setItem(progressionKey, JSON.stringify(updated));
    return updated;
  };

  const clearOrderProgression = (orderId) => {
    const progressionKey = `order_progression_${orderId}`;
    localStorage.removeItem(progressionKey);
  };

  // Centralized payment status check
  const getPaymentStatus = (order) => {
    // If already marked as paid, it's paid
    if (order.isPaid === true) {
      return { needsPayment: false, isPaid: true };
    }
    
    // Check if payment method requires COD collection
    const isCODMethod = order.paymentMethod && 
      ['cod', 'cash', 'offline'].includes(order.paymentMethod.toLowerCase());
    
    return {
      needsPayment: isCODMethod && !order.isPaid,
      isPaid: order.isPaid || false
    };
  };

  useEffect(() => {
    const loadAgentData = async () => {
      const userId = sessionStorage.getItem('userId');
      
      let agent_id = sessionStorage.getItem('agent_id');
      
      if (!agent_id) {
        const storedUser = sessionStorage.getItem('user');
        if (storedUser) {
          const user = JSON.parse(storedUser);
          agent_id = user.agent_id;
        }
      }
  
      if (!agent_id) {
        console.log('No agent_id found, fetching by userId');
        try {
          const response = await axios.get(
            `${process.env.REACT_APP_API}/deliveryagent/agent-by-user/${userId}`,
            { headers: getAuthHeaders() }
          );
          
          if (response.data.success) {
            agent_id = response.data.data.agentId;
            sessionStorage.setItem('agent_id', agent_id);
            setAgentData(response.data.data);
            return;
          }
        } catch (error) {
          console.error('Error fetching agent by userId:', error);
          setError('Failed to load agent data');
          return;
        }
      }
  
      try {
        console.log('Fetching agent data for agent_id:', agent_id);
        
        const response = await axios.get(
          `${process.env.REACT_APP_API}/deliveryagent/delivery-agents/${agent_id}`,
          { headers: getAuthHeaders() }
        );
        
        console.log('Agent data loaded:', response.data);
        setAgentData(response.data);
      } catch (error) {
        console.error('Error fetching agent data:', error);
        setError('Failed to load agent data');
      }
    };
  
    loadAgentData();
  }, [navigate]);

  //  FIXED: Fetch ALL delivery orders (medicine + report delivery)
  const fetchOrders = async (filter = null) => {
    setLoading(true);
    setError(null);
    
    try {
      const userId = sessionStorage.getItem('userId');

      if (!userId) {
        throw new Error('User ID not found');
      }

      console.log(' Fetching ALL delivery orders for userId:', userId, 'with filter:', filter);

      const params = { 
        user_id: userId
      };
      
      if (filter) {
        params.filter = filter;
      }

      const response = await axios.get(
        `${process.env.REACT_APP_API}/deliveryagent/my-orders`,
        { 
          params,
          headers: getAuthHeaders()
        }
      );

      console.log(' All orders fetched:', response.data);
      
      //  FIXED: Show ALL orders (medicine + report delivery for delivery agents)
      const allOrders = response.data.data || [];
      
      console.log(` Total orders: ${allOrders.length}`);
      console.log('Order types:', allOrders.map(o => ({
        id: o.orderId,
        orderType: o.orderType,
        deliveryType: o.deliveryType
      })));
      
      setOrders(allOrders);
      
      if (response.data.agentInfo) {
        setAgentData(prev => ({
          ...prev,
          ...response.data.agentInfo
        }));
      }
    } catch (error) {
      console.error(' Error fetching orders:', error);
      setError(error.response?.data?.message || 'Failed to fetch orders');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);


  const determineNextScreen = (order) => {


    const progression = getOrderProgression(order.id);
    const paymentStatus = getPaymentStatus(order);
    
    console.log('Payment Status:', paymentStatus);
    console.log('Local Progression:', progression);

    //  Handle REPORT DELIVERY orders
    if (order.deliveryType === 'report') {
      console.log('→ This is a REPORT DELIVERY order');
      
      // Check if fully delivered
      if (order.orderStatus === 'report-delivered' || progression.deliveryCompleted) {
        console.log('→ Report delivery completed, showing summary');
        return 'deliverySummary';
      }

      // Check if picked up (out for delivery)
      const isPickedUp = order.orderStatus === 'report-out-for-delivery' || 
                         progression.pickupConfirmed;

      if (isPickedUp) {
        console.log('→ Report is out for delivery');

        // Check OTP and payment
        const needsOTPOrPayment = !progression.otpVerified || 
                                  (paymentStatus.needsPayment && !progression.paymentCollected);

        if (needsOTPOrPayment) {
          console.log('→ Needs OTP verification or payment collection');
          return 'paymentCollection';
        }

        console.log('→ Ready for final delivery');
        return 'deliveryVerification';
      }

      // Check if order details viewed
      if (progression.orderDetailsViewed) {
        console.log('→ Order details viewed, showing pickup confirmation');
        return 'pickupConfirmation';
      }

      console.log('→ Starting from order details');
      return 'orderDetails';
    }

    //  Handle MEDICINE orders (existing logic)
    if (order.orderType === 'medicine') {
      console.log('→ This is a MEDICINE order');

      // Check if order is already fully completed
      if (order.assignmentStatus === 'delivered' || 
          order.deliveryStatus === 'delivered' ||
          order.orderStatus === 'delivered' ||
          progression.deliveryCompleted) {
        console.log('→ Medicine order is completed, showing summary');
        return 'deliverySummary';
      }

      // Check if picked up
      const isPickedUp = order.deliveryStatus === 'picked_up' || 
                         order.assignmentStatus === 'picked_up' || 
                         progression.pickupConfirmed;

      if (isPickedUp) {
        console.log('→ Medicine order is picked up');

        // Check OTP and payment completion together
        const needsOTPOrPayment = !progression.otpVerified || 
                                  (paymentStatus.needsPayment && !progression.paymentCollected);

        if (needsOTPOrPayment) {
          console.log('→ Needs OTP verification or payment collection');
          return 'paymentCollection';
        }

        // Both OTP and payment complete, ready for final delivery
        console.log('→ OTP and payment complete, showing delivery verification');
        return 'deliveryVerification';
      }

      // Check if order details have been confirmed
      if (progression.orderDetailsViewed) {
        console.log('→ Order details viewed, showing pickup confirmation');
        return 'pickupConfirmation';
      }

      // DEFAULT: Show order details
      console.log('→ Starting from order details');
      return 'orderDetails';
    }

    // Fallback for unknown order types
    console.warn(' Unknown order type, defaulting to order details');
    return 'orderDetails';
  };

  const handleOrderSelect = (order) => {
    console.log('=== ORDER SELECTION ===');
    console.log('Selected Order:', order);
    console.log('Order Type:', order.orderType);
    console.log('Delivery Type:', order.deliveryType);
    
    //  FIXED: Accept both medicine and report delivery orders
    if (order.orderType !== 'medicine' && order.orderType !== 'booking') {
      setError('Invalid order type');
      return;
    }

    //  For delivery agents, ensure it's either medicine OR report delivery
    if (order.orderType === 'booking' && order.deliveryType !== 'report') {
      setError('This dashboard is for medicine and report deliveries only.');
      return;
    }
    
    setSelectedOrder(order);
    
    // Determine and navigate to the correct screen
    const nextScreen = determineNextScreen(order);
    setCurrentScreen(nextScreen);
  };

  const handleBackToOrders = () => {
    setCurrentScreen('orderList');
    setSelectedOrder(null);
    fetchOrders();
  };

  // Handle order updates and refresh
  const handleOrderUpdate = async (nextScreen = null, progressionUpdates = null) => {
    try {
      const userId = sessionStorage.getItem('userId');
      
      console.log('=== UPDATING ORDER ===');
      console.log('Current order ID:', selectedOrder?.id);
      console.log('Progression updates:', progressionUpdates);
      
      const response = await axios.get(
        `${process.env.REACT_APP_API}/deliveryagent/my-orders`,
        { 
          params: { 
            user_id: userId
          },
          headers: getAuthHeaders()
        }
      );

      const updatedOrder = response.data.data.find(
        o => o.id === selectedOrder.id
      );

      if (updatedOrder) {
        console.log(' Order updated from backend:', {
          isPaid: updatedOrder.isPaid,
          deliveryStatus: updatedOrder.deliveryStatus,
          assignmentStatus: updatedOrder.assignmentStatus,
          orderStatus: updatedOrder.orderStatus,
          deliveryType: updatedOrder.deliveryType
        });
        
        setSelectedOrder(updatedOrder);
        
        // Update local progression if provided
        if (progressionUpdates) {
          updateOrderProgression(selectedOrder.id, progressionUpdates);
        }
        
        // If no specific next screen provided, determine automatically
        if (!nextScreen) {
          nextScreen = determineNextScreen(updatedOrder);
        }
        
        console.log('→ Navigating to screen:', nextScreen);
      }

      if (nextScreen) {
        setCurrentScreen(nextScreen);
      }
    } catch (error) {
      console.error(' Error updating order:', error);
      setError('Failed to refresh order data');
    }
  };

  const renderScreen = () => {
    switch (currentScreen) {
      case 'orderList':
        return (
          <OrderList
            orders={orders}
            loading={loading}
            onOrderSelect={handleOrderSelect}
            onRefresh={fetchOrders}
          />
        );
      
      case 'orderDetails':
        return (
          <OrderDetails
            order={selectedOrder}
            onBack={handleBackToOrders}
            onOrderUpdate={async () => {
              updateOrderProgression(selectedOrder.id, { orderDetailsViewed: true });
              await handleOrderUpdate('pickupConfirmation', { orderDetailsViewed: true });
            }}
          />
        );
      
      case 'pickupConfirmation':
        return (
          <PickupConfirmation
            order={selectedOrder}
            onBack={() => {
              setCurrentScreen('orderDetails');
            }}
            onPickupConfirmed={async () => {
              console.log(' Pickup confirmed for order:', selectedOrder.orderId);
              updateOrderProgression(selectedOrder.id, { pickupConfirmed: true });
              await handleOrderUpdate(null, { pickupConfirmed: true });
            }}
          />
        );
      
      case 'paymentCollection':
        return (
          <PaymentCollection
            order={selectedOrder}
            onBack={() => {
              setCurrentScreen('orderDetails');
            }}
            onPaymentConfirmed={async () => {
              console.log(' OTP and Payment confirmed for order:', selectedOrder.orderId);
              // Mark both OTP and payment as complete
              updateOrderProgression(selectedOrder.id, { 
                otpVerified: true,
                paymentCollected: true 
              });
              await handleOrderUpdate(null, { 
                otpVerified: true,
                paymentCollected: true 
              });
            }}
          />
        );
      
      case 'deliveryVerification':
        return (
          <DeliveryVerification
            order={selectedOrder}
            onBack={() => {
              const progression = getOrderProgression(selectedOrder.id);
              
              // Go back to payment collection if not complete
              if (!progression.otpVerified || !progression.paymentCollected) {
                setCurrentScreen('paymentCollection');
              } else {
                setCurrentScreen('orderDetails');
              }
            }}
            onDeliveryConfirmed={(deliveryData) => {
              console.log(' Delivery confirmed for order:', selectedOrder.orderId);
              updateOrderProgression(selectedOrder.id, { deliveryCompleted: true });
              setSelectedOrder({ ...selectedOrder, ...deliveryData });
              setCurrentScreen('deliverySummary');
              setTimeout(() => {
                clearOrderProgression(selectedOrder.id);
              }, 1000);
            }}
            onReportIssue={(order) => {
              console.log('Report issue clicked with order:', order);
              setSelectedOrder(order);
              setCurrentScreen('issueReporting');
            }}
            onCollectPayment={() => {
              setCurrentScreen('paymentCollection');
            }}
          />
        );
      
      case 'deliverySummary':
        return (
          <DeliverySummary
            order={selectedOrder}
            onBackToOrders={() => {
              if (selectedOrder?.id) {
                clearOrderProgression(selectedOrder.id);
              }
              handleBackToOrders();
            }}
          />
        );
      

      case 'issueReporting':
        return (
          <IssueReporting
            order={selectedOrder}
            onBack={() => setCurrentScreen('deliveryVerification')}
            onIssueSubmitted={() => {
              console.log(' Issue submitted successfully, clearing progression');
              if (selectedOrder?.id) {
                clearOrderProgression(selectedOrder.id);
              }
              handleBackToOrders();
            }}
          />
        );
      default:
        return (
          <Alert severity="error">
            Unknown screen: {currentScreen}
          </Alert>
        );
    }
  };

  return (
    <Box sx={{ bgcolor: '#f8fafc', minHeight: '100vh', py: 4 }}>
      {/* Error Alert */}
      {error && (
        <Container maxWidth="lg" sx={{ pt: 2 }}>
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        </Container>
      )}

      {/* Main Content */}
      <Container maxWidth="lg" sx={{ py: 3 }}>
        {renderScreen()}
      </Container>
    </Box>
  );
};

export default DeliveryAgentDashboard;